/**
 * Training Service
 * Handles training category data fetching and management
 * Mock/Real switching is handled at Service layer
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { mockTrainingCategories, mockTrainingItems, generateCategoryId, generateItemId } from './mock/mockTraining';
import { delay, generateId } from '../mock/mockData';
import type { 
  TrainingAudience,
  TrainingCategory,
  TrainingCategoryFormData,
  TrainingItem,
  TrainingItemFormData
} from '../../types/training';

const sortCategories = (categories: TrainingCategory[]): TrainingCategory[] =>
  [...categories]
    .map(category => ({
      ...category,
      items: category.items
        ? [...category.items].sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            return 0;
          })
        : [],
    }))
    .sort((a, b) => {
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      if (a.createdAt && b.createdAt) {
        return b.createdAt.getTime() - a.createdAt.getTime();
      }
      return 0;
    });

class TrainingService {
  /**
   * Get all training categories with their items
   */
  static async getTrainingCategories(audience: TrainingAudience): Promise<TrainingCategory[]> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return sortCategories(mockTrainingCategories.filter(category => category.audience === audience));
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: TrainingCategory[];
        message?: string;
      }>(`/admin/trainings/categories?audience=${audience}`);

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to fetch training categories');
      }

      return sortCategories(response.data);
    }
  }

  /**
   * Get training category by ID
   */
  static async getTrainingCategoryById(id: string): Promise<TrainingCategory | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return (
        mockTrainingCategories.find(
          (category) =>
            category.id === id ||
            category.categoryId === id
        ) || null
      );
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: TrainingCategory;
        message?: string;
      }>(`/admin/trainings/categories/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch training category');
      }

      return response.data || null;
    }
  }

  /**
   * Create a new training category
   */
  static async createTrainingCategory(
    data: TrainingCategoryFormData
  ): Promise<TrainingCategory> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      
      const now = new Date();
      const nextSequenceNumber = mockTrainingCategories.length + 1;
      const newCategory: TrainingCategory = {
        id: generateId(),
        categoryId: generateCategoryId(nextSequenceNumber),
        name: data.name,
        audience: data.audience,
        order: data.order || nextSequenceNumber,
        items: [],
        createdAt: now,
        createdAtText: now.toISOString().split('T')[0],
        updatedAt: now,
        updatedAtText: now.toISOString().split('T')[0],
      };
      
      mockTrainingCategories.push(newCategory);
      return newCategory;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        data: TrainingCategory;
        message?: string;
      }>('/admin/trainings/categories', data);

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to create training category');
      }

      return response.data;
    }
  }

  /**
   * Update a training category
   */
  static async updateTrainingCategory(
    id: string,
    data: Partial<TrainingCategoryFormData>
  ): Promise<TrainingCategory> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      
      const categoryIndex = mockTrainingCategories.findIndex(
        (c) => c.id === id || c.categoryId === id
      );
      
      if (categoryIndex === -1) {
        throw new Error('Training category not found');
      }
      
      const now = new Date();
      const updatedCategory: TrainingCategory = {
        ...mockTrainingCategories[categoryIndex],
        ...data,
        updatedAt: now,
        updatedAtText: now.toISOString().split('T')[0],
      };
      
      mockTrainingCategories[categoryIndex] = updatedCategory;
      return updatedCategory;
    } else {
      const response = await apiService.put<{
        isSuccess: boolean;
        data: TrainingCategory;
        message?: string;
      }>(`/admin/trainings/categories/${id}`, { ...data, id });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to update training category');
      }

      // If data is provided, return it. Otherwise, return a best-effort object or cast.
      // Since the backend returns { isSuccess: true } without data, we can just return
      // the input data merged with ID as a placeholder, or fetch fresh data if critical.
      // The frontend doesn't use the return value for anything other than success confirmation usually.
      return response.data || { id, ...data } as TrainingCategory;
    }
  }

  /**
   * Delete a training category (also deletes all items in the category)
   */
  static async deleteTrainingCategory(id: string): Promise<void> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      
      const category = mockTrainingCategories.find(
        (c) => c.id === id || c.categoryId === id
      );
      
      if (!category) {
        throw new Error('Training category not found');
      }
      
      // Remove category
      const categoryIndex = mockTrainingCategories.findIndex(
        (c) => c.id === id || c.categoryId === id
      );
      mockTrainingCategories.splice(categoryIndex, 1);
      
      // Remove all items in this category
      const categoryId = category.categoryId;
      const itemsToRemove = mockTrainingItems.filter(item => item.categoryId === categoryId);
      itemsToRemove.forEach(item => {
        const itemIndex = mockTrainingItems.findIndex(i => i.id === item.id);
        if (itemIndex !== -1) {
          mockTrainingItems.splice(itemIndex, 1);
        }
      });
    } else {
      const response = await apiService.delete<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/trainings/categories/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to delete training category');
      }
    }
  }

  /**
   * Get training items by category ID
   */
  static async getTrainingItemsByCategory(categoryId: string): Promise<TrainingItem[]> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return mockTrainingItems
        .filter(item => item.categoryId === categoryId)
        .sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return 0;
        });
    } else {
      // Assuming frontend logic fetches full detailed category which includes items, 
      // or we can implement a specific endpoint if needed.
      // Based on controller, Getting a category might return items or we use the detail query.
      // But typically RESTful would be nested or separate. The current controller doesn't list a specific "Get items by category" endpoint.
      // It has `GetCategories` which returns everything via `GetTrainingDetailQuery`.
      // So fetch all and filter client side OR ask backend to add this endpoint.
      // Given the `GetTrainingDetailQuery` likely returns the full tree, let's use that logic:
      // fetch all categories -> find specific category -> return items
      
      const response = await apiService.get<{
          isSuccess: boolean;
          data: TrainingCategory[];
          message?: string;
        }>('/admin/trainings/categories'); // Using the main query endpoint

        if (!response.isSuccess || !response.data) {
          throw new Error(response.message || 'Failed to fetch training items');
        }
        
        const category = response.data.find(c => c.id === categoryId);
        const items = category ? category.items || [] : [];
        
        return items.sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          return 0;
        });
    }
  }

  /**
   * Create a new training item
   */
  static async createTrainingItem(
    data: TrainingItemFormData
  ): Promise<TrainingItem> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      
      const category = mockTrainingCategories.find(
        c => c.id === data.categoryId || c.categoryId === data.categoryId
      );
      
      if (!category) {
        throw new Error('Training category not found');
      }
      
      const categoryId = category.categoryId;
      const itemsInCategory = mockTrainingItems.filter(item => item.categoryId === categoryId);
      const nextItemSequence = itemsInCategory.length + 1;
      
      const now = new Date();
      const newItem: TrainingItem = {
        id: generateId(),
        itemId: generateItemId(categoryId, nextItemSequence),
        title: data.title,
        description: data.description,
        link: data.link,
        categoryId: categoryId,
        hidden: false,
        order: data.order || nextItemSequence,
        createdAt: now,
        createdAtText: now.toISOString().split('T')[0],
        updatedAt: now,
        updatedAtText: now.toISOString().split('T')[0],
      };
      
      mockTrainingItems.push(newItem);
      
      // Update category items array
      if (!category.items) {
        category.items = [];
      }
      category.items.push(newItem);
      
      return newItem;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        data: TrainingItem;
        message?: string;
      }>('/admin/trainings', data); // Changed from /items to root for creation

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to create training item');
      }

      return response.data;
    }
  }

  /**
   * Update a training item
   */
  static async updateTrainingItem(
    id: string,
    data: Partial<TrainingItemFormData>
  ): Promise<TrainingItem> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      
      const itemIndex = mockTrainingItems.findIndex(
        (i) => i.id === id || i.itemId === id
      );
      
      if (itemIndex === -1) {
        throw new Error('Training item not found');
      }
      
      const now = new Date();
      const updatedItem: TrainingItem = {
        ...mockTrainingItems[itemIndex],
        ...data,
        updatedAt: now,
        updatedAtText: now.toISOString().split('T')[0],
      };
      
      mockTrainingItems[itemIndex] = updatedItem;
      
      // Update category items array
      const category = mockTrainingCategories.find(
        c => c.categoryId === updatedItem.categoryId
      );
      if (category && category.items) {
        const categoryItemIndex = category.items.findIndex(i => i.id === updatedItem.id);
        if (categoryItemIndex !== -1) {
          category.items[categoryItemIndex] = updatedItem;
        }
      }
      
      return updatedItem;
    } else {
      const response = await apiService.put<{
        isSuccess: boolean;
        data: TrainingItem;
        message?: string;
      }>(`/admin/trainings/${id}`, { ...data, id });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to update training item');
      }

      return response.data || { id, ...data } as TrainingItem;
    }
  }

  /**
   * Delete a training item
   */
  static async deleteTrainingItem(id: string): Promise<void> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      
      const item = mockTrainingItems.find(
        (i) => i.id === id || i.itemId === id
      );
      
      if (!item) {
        throw new Error('Training item not found');
      }
      
      const itemIndex = mockTrainingItems.findIndex(
        (i) => i.id === id || i.itemId === id
      );
      mockTrainingItems.splice(itemIndex, 1);
      
      // Remove from category items array
      const category = mockTrainingCategories.find(
        c => c.categoryId === item.categoryId
      );
      if (category && category.items) {
        const categoryItemIndex = category.items.findIndex(i => i.id === item.id);
        if (categoryItemIndex !== -1) {
          category.items.splice(categoryItemIndex, 1);
        }
      }
    } else {
      const response = await apiService.delete<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/trainings/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to delete training item');
      }
    }
  }

  /**
   * Hide a training item (admin only)
   */
  static async hideTrainingItem(id: string): Promise<void> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      const item = mockTrainingItems.find(
        (i) => i.id === id || i.itemId === id
      );

      if (!item) {
        throw new Error('Training item not found');
      }

      item.hidden = true;

      // Also update in category list if present
      const category = mockTrainingCategories.find(
        (c) => c.categoryId === item.categoryId
      );
      if (category?.items) {
        const categoryItem = category.items.find(i => i.id === item.id);
        if (categoryItem) {
          categoryItem.hidden = true;
        }
      }
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/trainings/${id}/hide`, {});

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to hide training item');
      }
    }
  }

  /**
   * Show a training item (admin only)
   */
  static async showTrainingItem(id: string): Promise<void> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      const item = mockTrainingItems.find(
        (i) => i.id === id || i.itemId === id
      );

      if (!item) {
        throw new Error('Training item not found');
      }

      item.hidden = false;

      // Also update in category list if present
      const category = mockTrainingCategories.find(
        (c) => c.categoryId === item.categoryId
      );
      if (category?.items) {
        const categoryItem = category.items.find(i => i.id === item.id);
        if (categoryItem) {
          categoryItem.hidden = false;
        }
      }
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/trainings/${id}/show`, {});

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to show training item');
      }
    }
  }
}

export { TrainingService };
